import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

// The create/update office endpoints return a `campaignFlowWebhook` block when
// the office carries Campaign Flow credentials: the backend registers this
// tenant's webhook against our receiver and captures the signing secret (which
// Campaign Flow only ever returns once). Surface the outcome so a failed or
// skipped registration isn't silent — the office itself still saves either way.
const reportWebhookProvisioning = (res) => {
  const result = res?.data?.campaignFlowWebhook;
  if (!result) return;

  if (result.status === "failed" || result.status === "skipped") {
    showToast(
      "error",
      `Campaign Flow webhook ${result.status}: ${result.reason || "unknown reason"}`,
    );
    return;
  }

  const messages = {
    created: "Campaign Flow webhook registered — signing secret captured.",
    repaired: "Campaign Flow webhook re-enabled and re-subscribed.",
    unchanged: "Campaign Flow webhook already registered.",
  };
  showToast(
    "success",
    messages[result.status] || "Campaign Flow webhook checked.",
  );
};

export const useGetOfficesQuery = (options = {}) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_OFFICES],
    queryFn: () => api.get(API_ROUTES.GET_OFFICES),
    ...options,
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useGetOfficeByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_OFFICE_BY_ID, id],
    queryFn: () => api.get(`${API_ROUTES.GET_OFFICE_BY_ID.replace(":id", id)}`),
    enabled: !!id,
  });
  return { data: data?.data?.data ?? null, isPending, isError, error };
};

export const useCreateOfficeMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (formData) =>
        api.post(API_ROUTES.CREATE_OFFICE, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      onSuccess: (res) => {
        showToast("success", "Office created successfully");
        reportWebhookProvisioning(res);
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_OFFICES);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateOfficeMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, formData }) =>
        api.put(`${API_ROUTES.UPDATE_OFFICE.replace(":id", id)}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
      onSuccess: (res) => {
        showToast("success", "Office updated successfully");
        reportWebhookProvisioning(res);
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_OFFICES]);
        queryClient.invalidateQueries([API_ROUTES.GET_OFFICE_BY_ID]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useDeleteOfficeMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.delete(`${API_ROUTES.DELETE_OFFICE.replace(":id", id)}`),
      onSuccess: () => {
        showToast("success", "Office deleted successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries([API_ROUTES.GET_OFFICES]);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Live REA/Domain capability check. Every call hits both portals, so it is
// manual only — `enabled: false` keeps it off the office read, and the panel's
// refresh button calls `refetch`.
export const useOfficePortalAccessQuery = (id) => {
  const { data, isFetching, isError, error, refetch, isFetched } = useQuery({
    queryKey: [API_ROUTES.GET_OFFICE_PORTAL_ACCESS, id],
    queryFn: () =>
      api.get(API_ROUTES.GET_OFFICE_PORTAL_ACCESS.replace(":id", id)),
    enabled: false,
    staleTime: 0,
  });
  return {
    data: data?.data?.data ?? null,
    isFetching,
    isError,
    error,
    refetch,
    isFetched,
  };
};
